import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, AlertTriangle, Phone, User, Building2, Mail, CheckCircle2, HandHeart } from 'lucide-react';

import { API } from '../lib/apiBase';

const DECISION_META = {
  keep: { label: 'Stays with this org', color: 'bg-[#DCFCE7] text-[#166534] border-[#86EFAC]', icon: CheckCircle2 },
  release: { label: 'Went to the other org', color: 'bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]', icon: HandHeart },
  pending: { label: 'Pending', color: 'bg-[#F5F5F5] text-[#595959] border-[#D1D5DB]', icon: AlertTriangle },
};

export default function SharedPortalPage() {
  const { organization, isOperator } = useAuth();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [savingDecision, setSavingDecision] = useState(false);

  const fetchSharedBeneficiaries = useCallback(async () => {
    try {
      const [bRes, rRes] = await Promise.all([
        axios.get(`${API}/shared-portal/beneficiaries`),
        axios.get(`${API}/duplicate-resolutions`).catch(() => ({ data: [] })),
      ]);
      setBeneficiaries(bRes.data);
      setResolutions(rRes.data || []);
    } catch (error) {
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedBeneficiaries();
  }, [fetchSharedBeneficiaries]);

  const getDuplicateType = (beneficiary) => {
    const others = beneficiaries.filter(b => 
      b.id !== beneficiary.id && 
      b.organization_id !== beneficiary.organization_id
    );

    const phoneMatch = others.find(b => 
      b.phone_last5 && beneficiary.phone_last5 && 
      b.phone_last5 === beneficiary.phone_last5
    );
    if (phoneMatch) {
      return { type: 'phone', match: phoneMatch };
    }

    const nameMatch = others.find(b => 
      b.first_name && beneficiary.first_name && 
      b.first_name.toLowerCase() === beneficiary.first_name.toLowerCase()
    );
    if (nameMatch) {
      return { type: 'name', match: nameMatch };
    }

    return null;
  };

  const handleRowClick = (beneficiary) => {
    const duplicate = getDuplicateType(beneficiary);
    if (duplicate) {
      setDuplicateInfo({
        beneficiary,
        duplicate: duplicate.match,
        type: duplicate.type
      });
    }
  };

  const pairKey = useMemo(() => {
    if (!duplicateInfo) return null;
    const ids = [duplicateInfo.beneficiary.id, duplicateInfo.duplicate.id].sort();
    return ids.join('::');
  }, [duplicateInfo]);

  const currentResolution = useMemo(() => {
    if (!pairKey) return null;
    return resolutions.find((r) => r.pair_key === pairKey) || null;
  }, [pairKey, resolutions]);

  // Which of the two beneficiaries in the pair belongs to the current org, if any
  const myBeneficiaryInPair = useMemo(() => {
    if (!duplicateInfo || !organization?.id) return null;
    if (duplicateInfo.beneficiary.organization_id === organization.id) return duplicateInfo.beneficiary;
    if (duplicateInfo.duplicate.organization_id === organization.id) return duplicateInfo.duplicate;
    return null;
  }, [duplicateInfo, organization]);

  const otherBeneficiaryInPair = useMemo(() => {
    if (!duplicateInfo || !myBeneficiaryInPair) return null;
    return myBeneficiaryInPair.id === duplicateInfo.beneficiary.id
      ? duplicateInfo.duplicate
      : duplicateInfo.beneficiary;
  }, [duplicateInfo, myBeneficiaryInPair]);

  const myDecision = currentResolution?.decisions?.[organization?.id]?.decision || 'pending';
  const otherDecision = otherBeneficiaryInPair
    ? currentResolution?.decisions?.[otherBeneficiaryInPair.organization_id]?.decision || 'pending'
    : null;

  // Compute conflict/resolved based on BOTH parties' decisions in the pair, independent
  // of whether the current user is one of them (so the status is visible to any viewer).
  const pairDecisions = useMemo(() => {
    if (!duplicateInfo || !currentResolution?.decisions) return { a: 'pending', b: 'pending' };
    return {
      a: currentResolution.decisions[duplicateInfo.beneficiary.organization_id]?.decision || 'pending',
      b: currentResolution.decisions[duplicateInfo.duplicate.organization_id]?.decision || 'pending',
    };
  }, [duplicateInfo, currentResolution]);

  const conflict = pairDecisions.a === 'keep' && pairDecisions.b === 'keep';
  const resolved = (pairDecisions.a === 'keep' && pairDecisions.b === 'release') ||
                   (pairDecisions.a === 'release' && pairDecisions.b === 'keep');

  const saveDecision = async (decision) => {
    if (!myBeneficiaryInPair || !otherBeneficiaryInPair) return;
    setSavingDecision(true);
    try {
      await axios.post(`${API}/duplicate-resolutions/${myBeneficiaryInPair.id}`, {
        duplicate_beneficiary_id: otherBeneficiaryInPair.id,
        decision,
      });
      toast.success('Decision saved. Both organizations can now see it.');
      // Refresh resolutions
      const r = await axios.get(`${API}/duplicate-resolutions`);
      setResolutions(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save decision');
    } finally {
      setSavingDecision(false);
    }
  };

  const filteredBeneficiaries = beneficiaries.filter(b =>
    b.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.phone_last5?.includes(searchTerm) ||
    b.child_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const orgStats = beneficiaries.reduce((acc, b) => {
    acc[b.organization_name] = (acc[b.organization_name] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="shared-portal-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Shared Portal</h1>
        <p className="text-[#595959] mt-1 text-sm md:text-base">
          View beneficiaries from all organizations
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-[#E5E5E5]">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs md:text-sm text-[#8C8C8C]">Total Beneficiaries</p>
            <p className="text-xl md:text-2xl font-bold text-[#1A1A1A] font-['Manrope']">
              {beneficiaries.length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E5]">
          <CardContent className="p-3 md:p-4">
            <p className="text-xs md:text-sm text-[#8C8C8C]">Organizations</p>
            <p className="text-xl md:text-2xl font-bold text-[#1A1A1A] font-['Manrope']">
              {Object.keys(orgStats).length}
            </p>
          </CardContent>
        </Card>
        {Object.entries(orgStats).slice(0, 2).map(([name, count]) => (
          <Card key={name} className="border-[#E5E5E5]">
            <CardContent className="p-3 md:p-4">
              <p className="text-xs md:text-sm text-[#8C8C8C] truncate">{name}</p>
              <p className="text-xl md:text-2xl font-bold text-[#4A5D4E] font-['Manrope']">{count}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 md:gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[#FFEBEE] border-l-4 border-[#D34848]" />
          <span className="text-[#595959]">Same phone number</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[#FFF8E1] border-l-4 border-[#E0A838]" />
          <span className="text-[#595959]">Same first name</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
        <Input
          placeholder="Search by name, phone, organization..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="shared-search-input"
        />
      </div>

      {/* Mobile Card View (hidden on md+) */}
      <div className="md:hidden space-y-2" data-testid="shared-mobile-list">
        {loading ? (
          <Card className="border-[#E5E5E5]"><CardContent className="py-8 text-center text-[#8C8C8C]">Loading...</CardContent></Card>
        ) : filteredBeneficiaries.length === 0 ? (
          <Card className="border-[#E5E5E5]"><CardContent className="py-8 text-center text-[#8C8C8C]">No beneficiaries found</CardContent></Card>
        ) : (
          filteredBeneficiaries.map((beneficiary) => {
            const duplicate = getDuplicateType(beneficiary);
            const borderClass = duplicate?.type === 'phone'
              ? 'border-l-4 border-l-[#D34848] bg-[#FFEBEE]/40'
              : duplicate?.type === 'name'
                ? 'border-l-4 border-l-[#E0A838] bg-[#FFF8E1]/40'
                : 'border-l-4 border-l-transparent';
            const isMine = beneficiary.organization_id === organization?.id;
            return (
              <Card
                key={beneficiary.id}
                className={`border border-[#E5E5E5] ${borderClass} ${duplicate ? 'cursor-pointer active:bg-[#F5F5F5]' : ''}`}
                onClick={() => handleRowClick(beneficiary)}
                data-testid={`shared-row-${beneficiary.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-[#1A1A1A] text-sm">
                          {beneficiary.first_name} {beneficiary.surname_initial}.
                        </span>
                        {isMine && <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-[#E9EFEA] text-[#4A5D4E] border-[#CDD9CF]">Yours</Badge>}
                        {duplicate?.type === 'phone' && <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-[#FDECEC] text-[#B91C1C] border-[#F5C6C6]">Same phone</Badge>}
                        {duplicate?.type === 'name' && <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-[#FFF8E1] text-[#B45309] border-[#FCD9A1]">Same name</Badge>}
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-[#595959]">
                        <Building2 className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{beneficiary.organization_name}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[#595959]">
                        {beneficiary.phone_last5 && <span className="font-mono">***{beneficiary.phone_last5}</span>}
                        {beneficiary.child_first_name && <span>Child: {beneficiary.child_first_name}</span>}
                        {beneficiary.child_birth_month_year && <span>{beneficiary.child_birth_month_year}</span>}
                        {beneficiary.diaper_size && <span>Size {beneficiary.diaper_size}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Desktop Table (hidden on mobile) */}
      <Card className="border-[#E5E5E5] hidden md:block">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F0EFEA]">
                  <TableHead className="font-medium">Organization</TableHead>
                  <TableHead className="font-medium">Last 5 Digits Phone</TableHead>
                  <TableHead className="font-medium">First Name</TableHead>
                  <TableHead className="font-medium">Surname Initial</TableHead>
                  <TableHead className="font-medium">1st Child Name</TableHead>
                  <TableHead className="font-medium">Birth (MM/YYYY)</TableHead>
                  <TableHead className="font-medium">Diaper Size</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[#8C8C8C]">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredBeneficiaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[#8C8C8C]">
                      No beneficiaries found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBeneficiaries.map((beneficiary) => {
                    const duplicate = getDuplicateType(beneficiary);
                    const rowClass = duplicate?.type === 'phone' 
                      ? 'duplicate-phone cursor-pointer' 
                      : duplicate?.type === 'name' 
                        ? 'duplicate-name cursor-pointer' 
                        : '';
                    
                    return (
                      <TableRow 
                        key={beneficiary.id}
                        className={rowClass}
                        onClick={() => handleRowClick(beneficiary)}
                        data-testid={`shared-row-desk-${beneficiary.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {beneficiary.organization_id === organization?.id && (
                              <Badge variant="outline" className="text-xs">Yours</Badge>
                            )}
                            <span className="truncate max-w-[150px]">{beneficiary.organization_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">{beneficiary.phone_last5}</TableCell>
                        <TableCell>{beneficiary.first_name}</TableCell>
                        <TableCell>{beneficiary.surname_initial}</TableCell>
                        <TableCell>{beneficiary.child_first_name || '-'}</TableCell>
                        <TableCell>{beneficiary.child_birth_month_year || '-'}</TableCell>
                        <TableCell>{beneficiary.diaper_size || '-'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Duplicate Info Modal */}
      <Dialog open={!!duplicateInfo} onOpenChange={() => setDuplicateInfo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${duplicateInfo?.type === 'phone' ? 'text-[#D34848]' : 'text-[#E0A838]'}`} />
              Possible Duplicate Found
            </DialogTitle>
            <DialogDescription>Information about potentially duplicate beneficiary</DialogDescription>
          </DialogHeader>
          
          {duplicateInfo && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${duplicateInfo.type === 'phone' ? 'bg-[#FFEBEE]' : 'bg-[#FFF8E1]'}`}>
                <p className="text-sm font-medium mb-2">
                  {duplicateInfo.type === 'phone' 
                    ? 'Same phone number found:' 
                    : 'Same name found:'}
                </p>
                <div className="flex items-center gap-2 text-sm">
                  {duplicateInfo.type === 'phone' ? (
                    <Phone className="w-4 h-4" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <span className="font-medium">
                    {duplicateInfo.type === 'phone' 
                      ? duplicateInfo.beneficiary.phone_last5 
                      : duplicateInfo.beneficiary.first_name}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="p-4 border rounded-lg">
                  <p className="text-xs text-[#8C8C8C] mb-2">Selected beneficiary</p>
                  <p className="font-medium">{duplicateInfo.beneficiary.first_name} {duplicateInfo.beneficiary.surname_initial}.</p>
                  <p className="text-sm text-[#595959] flex items-center gap-1 mt-2">
                    <Building2 className="w-3 h-3" />
                    {duplicateInfo.beneficiary.organization_name}
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-xs text-[#8C8C8C] mb-2">Possible duplicate in</p>
                  <p className="font-medium">{duplicateInfo.duplicate.first_name} {duplicateInfo.duplicate.surname_initial}.</p>
                  <p className="text-sm text-[#595959] flex items-center gap-1 mt-2">
                    <Building2 className="w-3 h-3" />
                    {duplicateInfo.duplicate.organization_name}
                  </p>
                </div>
              </div>

              <div className="bg-[#F0EFEA] rounded-lg p-4">
                <p className="text-sm font-medium mb-3">Contact organization:</p>
                <p className="text-[#4A5D4E] font-semibold text-lg">{duplicateInfo.duplicate.organization_name}</p>
                
                <div className="mt-3 space-y-2">
                  {duplicateInfo.duplicate.organization_email && (
                    <a 
                      href={`mailto:${duplicateInfo.duplicate.organization_email}`}
                      className="flex items-center gap-2 text-sm text-[#4A5D4E] hover:underline"
                    >
                      <Mail className="w-4 h-4" />
                      {duplicateInfo.duplicate.organization_email}
                    </a>
                  )}
                  {duplicateInfo.duplicate.organization_phone && (
                    <a 
                      href={`tel:${duplicateInfo.duplicate.organization_phone}`}
                      className="flex items-center gap-2 text-sm text-[#4A5D4E] hover:underline"
                    >
                      <Phone className="w-4 h-4" />
                      {duplicateInfo.duplicate.organization_phone}
                    </a>
                  )}
                  {!duplicateInfo.duplicate.organization_email && !duplicateInfo.duplicate.organization_phone && (
                    <p className="text-sm text-[#8C8C8C] italic">
                      No contact information available for this organization.
                    </p>
                  )}
                </div>
              </div>

              {/* Beneficiary choice resolution — always rendered */}
              <div className="border border-[#E5E5E5] rounded-lg p-4 space-y-3" data-testid="duplicate-resolution-panel">
                <div>
                  <p className="font-semibold text-[#1A1A1A] text-sm">Beneficiary's choice</p>
                  <p className="text-xs text-[#8C8C8C] mt-0.5">
                    {myBeneficiaryInPair
                      ? 'Record what the beneficiary decided. Both organizations will see the outcome.'
                      : 'Your organization is not part of this pair, so you can only view the current decisions.'}
                  </p>
                </div>

                {/* Combined status badges — show BOTH organizations' names and decisions */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="text-[#595959] font-medium">
                      {duplicateInfo.beneficiary.organization_name}:
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        DECISION_META[
                          currentResolution?.decisions?.[duplicateInfo.beneficiary.organization_id]
                            ?.decision || 'pending'
                        ].color
                      }
                    >
                      {
                        DECISION_META[
                          currentResolution?.decisions?.[duplicateInfo.beneficiary.organization_id]
                            ?.decision || 'pending'
                        ].label
                      }
                    </Badge>
                    {duplicateInfo.beneficiary.organization_id === organization?.id && (
                      <span className="text-[10px] text-[#4A5D4E] font-medium">(you)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="text-[#595959] font-medium">
                      {duplicateInfo.duplicate.organization_name}:
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        DECISION_META[
                          currentResolution?.decisions?.[duplicateInfo.duplicate.organization_id]
                            ?.decision || 'pending'
                        ].color
                      }
                    >
                      {
                        DECISION_META[
                          currentResolution?.decisions?.[duplicateInfo.duplicate.organization_id]
                            ?.decision || 'pending'
                        ].label
                      }
                    </Badge>
                    {duplicateInfo.duplicate.organization_id === organization?.id && (
                      <span className="text-[10px] text-[#4A5D4E] font-medium">(you)</span>
                    )}
                  </div>
                </div>

                {conflict && (
                  <div className="bg-[#FDECEC] border border-[#F5C6C6] rounded-md p-3 flex gap-2" data-testid="resolution-conflict">
                    <AlertTriangle className="w-4 h-4 text-[#B91C1C] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#B91C1C]">
                      <span className="font-semibold">Conflict:</span> both organizations declared that the
                      beneficiary stays with them. Please coordinate directly to confirm.
                    </p>
                  </div>
                )}

                {resolved && (
                  <div className="bg-[#DCFCE7] border border-[#86EFAC] rounded-md p-3 flex gap-2" data-testid="resolution-resolved">
                    <CheckCircle2 className="w-4 h-4 text-[#166534] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#166534]">
                      <span className="font-semibold">Resolved.</span> Both sides are aligned on the decision.
                    </p>
                  </div>
                )}

                {/* Decision controls — only visible when the current org is one of the pair */}
                {myBeneficiaryInPair && otherBeneficiaryInPair ? (
                  isOperator ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1">
                      <Button
                        size="sm"
                        variant={myDecision === 'keep' ? 'default' : 'outline'}
                        className={myDecision === 'keep' ? 'bg-[#166534] hover:bg-[#14532D]' : ''}
                        onClick={() => saveDecision('keep')}
                        disabled={savingDecision}
                        data-testid="decision-keep-button"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                        Stays with us
                      </Button>
                      <Button
                        size="sm"
                        variant={myDecision === 'release' ? 'default' : 'outline'}
                        className={myDecision === 'release' ? 'bg-[#0369A1] hover:bg-[#075985]' : ''}
                        onClick={() => saveDecision('release')}
                        disabled={savingDecision}
                        data-testid="decision-release-button"
                      >
                        <HandHeart className="w-3.5 h-3.5 mr-1.5" />
                        Went to other org
                      </Button>
                      <Button
                        size="sm"
                        variant={myDecision === 'pending' ? 'default' : 'outline'}
                        onClick={() => saveDecision('pending')}
                        disabled={savingDecision}
                        data-testid="decision-pending-button"
                      >
                        Clear
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-[#8C8C8C] italic">Only operators and admins can set the decision.</p>
                  )
                ) : (
                  <p
                    className="text-xs text-[#8C8C8C] italic bg-[#FAFAF8] rounded p-2 border border-dashed border-[#E5E5E5]"
                    data-testid="resolution-readonly-note"
                  >
                    Only {duplicateInfo.beneficiary.organization_name} and {duplicateInfo.duplicate.organization_name} can
                    record their decision for this duplicate.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
